const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * The API's own origin, for the places that must show it rather than call it —
 * the MCP endpoint an operator hands to their agent. Empty when the API is
 * served from this origin, so fall back to the page's.
 */
export const API_BASE_URL = API_URL || window.location.origin;

class NetworkError extends Error {
  constructor() {
    super('Network error - please check your connection');
    this.name = 'NetworkError';
  }
}

class ApiError extends Error {
  status: number;
  /** A machine-readable reason, when the server gave one: branch on this, not on the message. */
  code?: string;
  /** The whole body of the answer, for the errors that say more than a sentence (who holds a lock). */
  body?: Record<string, unknown>;

  constructor(message: string, status: number, code?: string, body?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('token');

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  let response: Response;

  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });
  } catch {
    throw new NetworkError();
  }

  // A 401 on an authenticated call means the session died: clear it and start
  // over. A 401 on an anonymous call is just a rejected credential (a wrong
  // password on the login or invite-accept form) and belongs to the caller.
  if (response.status === 401 && token) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new ApiError('Session expired', 401);
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(
      error.error || `HTTP ${response.status}`,
      response.status,
      typeof error.code === 'string' ? error.code : undefined,
      error
    );
  }

  return response.json();
}

export const api = {
  get: <T>(endpoint: string) => request<T>(endpoint),

  post: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),

  put: <T>(endpoint: string, body: unknown) =>
    request<T>(endpoint, { method: 'PUT', body: JSON.stringify(body) }),

  delete: <T>(endpoint: string) => request<T>(endpoint, { method: 'DELETE' }),
};

export { NetworkError, ApiError };
